import { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Subscribes to /api/ws/questions. Returns latest queue + featured question.
 * Polling-free thanks to FastAPI WebSocket broadcaster.
 */
export const useQuestionsWS = () => {
    const [queue, setQueue] = useState([]);
    const [featured, setFeatured] = useState(null);
    const [recentAnswered, setRecentAnswered] = useState([]);
    const wsRef = useRef(null);
    const reconnectRef = useRef(null);

    useEffect(() => {
        const url = BACKEND_URL.replace(/^http/, "ws") + "/api/ws/questions";
        let closed = false;
        let heartbeat = null;

        const connect = () => {
            let ws;
            try {
                ws = new WebSocket(url);
            } catch {
                reconnectRef.current = setTimeout(connect, 4000);
                return;
            }
            wsRef.current = ws;
            ws.onmessage = (e) => {
                try {
                    const msg = JSON.parse(e.data);
                    if (msg.type === "QUEUE_UPDATED") setQueue(msg.data || []);
                    if (msg.type === "DISPLAY_OVERLAY") setFeatured(msg.data || null);
                    if (msg.type === "REMOVE_OVERLAY") setFeatured(null);
                    if (msg.type === "QUESTION_ANSWERED" && msg.data) {
                        setRecentAnswered((prev) => {
                            if (prev.some((q) => q.id === msg.data.id)) return prev;
                            return [msg.data, ...prev].slice(0, 20);
                        });
                    }
                } catch {
                    /* ignore */
                }
            };
            ws.onopen = () => {
                heartbeat = setInterval(() => {
                    if (ws.readyState === 1) {
                        try {
                            ws.send("ping");
                        } catch {
                            /* ignore */
                        }
                    }
                }, 25000);
            };
            ws.onclose = () => {
                if (heartbeat) clearInterval(heartbeat);
                if (!closed) reconnectRef.current = setTimeout(connect, 4000);
            };
            ws.onerror = () => {
                try {
                    ws.close();
                } catch {
                    /* ignore */
                }
            };
        };
        connect();

        return () => {
            closed = true;
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            if (heartbeat) clearInterval(heartbeat);
            const ws = wsRef.current;
            if (ws) {
                try {
                    ws.close();
                } catch {
                    /* ignore */
                }
            }
        };
    }, []);

    return { queue, featured, setQueue, recentAnswered, setRecentAnswered };
};
